"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Building2, Waves, House as Home, ShieldCheck, ClipboardCheck, FileCheck2, CircleCheck as CheckCircle2, ArrowRight, Sparkles, MapPin } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const accessContexts = [{
  title: "Period & conservation buildings",
  subtitle: "Protecting historic fabric",
  description: "Carefully planned tie patterns, non-marking protective sleeves, and distributed ground load plates to prevent damage to fragile brickwork, lime mortars, and heritage masonry.",
  icon: Building2,
  points: ["Non-intrusive structural anchors", "Protective foam and timber ground pads", "Tailored to local conservation guidance"]
}, {
  title: "Riverside & constrained water edges",
  subtitle: "Thames-side access solutions",
  description: "Specialist cantilevered sections and bridging scaffolds designed for gardens and boathouses backing directly onto the River Thames where ground access is limited.",
  icon: Waves,
  points: ["Waterfront bridging and beam runs", "Enhanced debris netting over waterways", "Careful maneuvering on narrow lawns"]
}, {
  title: "High-value residential homes",
  subtitle: "Discreet and respectful site care",
  description: "Clean presentation, polite site conduct, and protective sheeting for manicured landscaping, driveways, and private architectural finishes throughout the SL7 district, Bisham, and riverside properties.",
  icon: Home,
  points: ["Driveway protection boards as standard", "Neat monoflex and acoustic sheeting", "Punctual, tidy, and courteous crew"]
}];
const safetySteps = [{
  step: "01",
  title: "Rigorous site assessment",
  description: "Every Marlow project begins with a pre-erection survey to evaluate ground stability, building clearances, overhead obstacles, and property access.",
  icon: ClipboardCheck
}, {
  step: "02",
  title: "Safe and compliant access",
  description: "Structures are built strictly to TG20 guidelines by fully qualified operatives, incorporating integrated edge protection, double guardrails, and secure ladder gates.",
  icon: ShieldCheck
}, {
  step: "03",
  title: "Handover inspection & tags",
  description: "A formal handover certificate and clear Scafftag are issued before works commence, followed by mandatory statutory 7-day re-inspections.",
  icon: FileCheck2
}];
const accreditations = [{
  name: "NASC",
  title: "National Access & Scaffolding Confederation",
  description: "Audited for industry safety standards, training, and technical competence."
}, {
  name: "CHAS",
  title: "Contractors Health and Safety Assessment Scheme",
  description: "Verified health and safety compliance management."
}];
const nearbyAreas = [{
  name: "High Wycombe",
  to: "/scaffolding-in-high-wycombe"
}, {
  name: "Maidenhead",
  to: "/scaffolding-in-maidenhead"
}, {
  name: "Beaconsfield",
  to: "/scaffolding-in-beaconsfield"
}];
export default function MarlowAccess() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="marlowaccessandsafety" data-nav="light" className="bg-background py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        
        {/* Section Header */}
        <div className="max-w-3xl mb-14 md:mb-20">
          <div className="inline-flex items-center gap-2 mb-3">
            <Badge variant="outline" className="text-foreground border-border bg-accent/50 px-3 py-1 text-xs">
              <Sparkles className="size-3 text-primary" />
              Marlow & South Bucks Access
            </Badge>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-default text-foreground tracking-tight mb-4">
            Scaffolding access engineered for Marlow properties
          </h2>
          <p className="text-lg text-muted-foreground font-sans leading-relaxed">
            We provide secure working platforms across SL7—the principal Marlow district—and surrounding Thames riverside locations, protecting delicate facades, heritage masonry, and private grounds with safety at every stage.
          </p>
        </div>

        {/* Marlow Specific Context Cards */}
        <div className="mb-20">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h3 className="text-2xl font-bold font-default text-foreground">
                Tailored solutions for challenging locations
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                Designed specifically around Marlow&apos;s architectural characteristics and riverfront terrain.
              </p>
            </div>
          </div>

          <motion.div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }}>
            {accessContexts.map((context, i) => {
            const IconComponent = context.icon;
            return <motion.div key={context.title} variants={itemVariants} data-index={i} className="h-full">
                  <Card className="h-full border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-200">
                    <CardHeader className="pb-4">
                      <div className="size-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-4">
                        <IconComponent className="size-6" />
                      </div>
                      <Badge variant="secondary" className="w-fit text-xs mb-1">
                        {context.subtitle}
                      </Badge>
                      <CardTitle className="text-xl font-bold text-foreground font-default">
                        {context.title}
                      </CardTitle>
                      <CardDescription className="text-muted-foreground text-sm leading-relaxed mt-2">
                        {context.description}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <ul className="space-y-2.5 border-t border-border pt-4">
                        {context.points.map((point, pointIndex) => <li key={pointIndex} className="flex items-start gap-2.5 text-sm text-foreground/90">
                            <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                            <span>{point}</span>
                          </li>)}
                      </ul>
                    </CardContent>
                  </Card>
                </motion.div>;
          })}
          </motion.div>
        </div>

        {/* Safety & Compliance Framework */}
        <div className="bg-card rounded-2xl border border-border p-6 sm:p-10 lg:p-12 shadow-sm mb-16">
          <div className="max-w-3xl mb-12">
            <Badge variant="outline" className="mb-3 border-border text-foreground">
              Safety-led execution
            </Badge>
            <h3 className="text-2xl sm:text-3xl font-bold font-default text-foreground mb-3">
              Standard 3-step safety protocol
            </h3>
            <p className="text-muted-foreground text-base leading-relaxed">
              Every access system follows a transparent inspection sequence from initial risk evaluation to official sign-off.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
            {safetySteps.map((step, i) => {
            const StepIcon = step.icon;
            return <div key={step.step} data-index={i} className="flex flex-col relative">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-3xl font-bold font-mono text-primary/30">
                      {step.step}
                    </span>
                    <div className="size-10 rounded-lg bg-accent text-accent-foreground flex items-center justify-center">
                      <StepIcon className="size-5 text-primary" />
                    </div>
                  </div>
                  <h4 className="text-lg font-semibold text-foreground mb-2 font-default">
                    {step.title}
                  </h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {step.description}
                  </p>
                </div>;
          })}
          </div>
        </div>

        {/* Accreditations & Site Visit CTA */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center mb-16">
          <div className="lg:col-span-7">
            <h4 className="text-xl font-bold text-foreground mb-2 font-default">
              Confirmed safety accreditations
            </h4>
            <p className="text-muted-foreground text-sm mb-6">
              Our operations are verified through independent, nationally recognized health and safety frameworks.
            </p>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {accreditations.map((acc, i) => <div key={acc.name} data-index={i} className="p-5 rounded-xl border border-border bg-accent/40 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-lg font-bold font-default text-foreground tracking-wide">
                        {acc.name}
                      </span>
                      <ShieldCheck className="size-5 text-primary" />
                    </div>
                    <p className="text-xs font-medium text-foreground mb-1">
                      {acc.title}
                    </p>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {acc.description}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>

          <div className="lg:col-span-5 bg-secondary text-secondary-foreground rounded-2xl p-6 sm:p-8 flex flex-col justify-between h-full shadow-md">
            <div>
              <span className="text-xs font-medium tracking-wider uppercase text-primary-foreground/80 mb-2 block">
                Direct Contact
              </span>
              <h4 className="text-xl sm:text-2xl font-bold font-default text-secondary-foreground mb-3">
                Planning a project in Marlow (SL7)?
              </h4>
              <p className="text-sm text-secondary-foreground/80 leading-relaxed mb-6">
                Arrange a site visit with our team to review access constraints, structural protections, and project timescales across the SL7 district.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button asChild size="lg" className="w-full sm:w-auto font-medium">
                <Link to="/ContactRequestQuotePage">
                  Request site assessment
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto bg-secondary border-border text-secondary-foreground hover:bg-secondary/80">
                <Link to="/HealthSafetyAccreditationsPage">
                  Safety details
                </Link>
              </Button>
            </div>
          </div>
        </div>

        {/* Nearby Operational Coverage Links */}
        <div className="pt-8 border-t border-border">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-5 bg-card border border-border rounded-xl p-5 sm:p-6 shadow-xs">
            <div className="flex items-center gap-3">
              <div className="size-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                <MapPin className="size-5" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-foreground uppercase tracking-wide font-default">
                  Coverage around Marlow (SL7)
                </h4>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Fast scaffolding deployment across surrounding Thames Valley and Buckinghamshire towns:
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              {nearbyAreas.map((area, i) => <Button key={area.name} asChild variant="outline" size="sm" data-index={i} className="bg-background border-border text-foreground hover:bg-accent hover:text-accent-foreground text-xs">
                  <Link to={area.to}>
                    {area.name}
                  </Link>
                </Button>)}
              <Button asChild variant="ghost" size="sm" className="text-primary hover:text-primary hover:bg-primary/10 text-xs font-semibold">
                <Link to="#" className="inline-flex items-center gap-1">
                  View all areas
                  <ArrowRight className="size-3" />
                </Link>
              </Button>
            </div>
          </div>
        </div>

      </div>
    </section>;
}